<template>
  <div class="w-full h-auto min-h-screen mt-3 justify-between bg-white space-y-9 rounded-t-[20px]">
    <HeaderComponent page-title="Pricing" />
    <div class="mx-8 my-8">
      <div class="text-center mb-12">
        <h1 class="text-4xl font-bold text-primaryDark mb-4">Pricing Plans</h1>
        <p class="text-grayDark text-lg">Choose the perfect plan for your business needs</p>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        <!-- Basic Plan -->
        <div class="bg-white border border-grayLight rounded-lg p-8 text-center">
          <h3 class="text-2xl font-bold text-primaryDark mb-4">Basic</h3>
          <div class="mb-6">
            <span class="text-4xl font-bold text-primaryDark">$99</span>
            <span class="text-grayDark">/month</span>
          </div>
          <ul class="text-left space-y-3 mb-8">
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Up to 5 projects
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Basic team management
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Email support
            </li>
          </ul>
          <button class="w-full py-3 px-6 border border-primaryDark text-primaryDark rounded-lg hover:bg-primaryDark hover:text-white transition-colors">
            Get Started
          </button>
        </div>

        <!-- Pro Plan -->
        <div class="bg-white border-2 border-primaryDark rounded-lg p-8 text-center relative">
          <div class="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span class="bg-primaryDark text-white px-4 py-1 rounded-full text-sm">Most Popular</span>
          </div>
          <h3 class="text-2xl font-bold text-primaryDark mb-4">Professional</h3>
          <div class="mb-6">
            <span class="text-4xl font-bold text-primaryDark">$199</span>
            <span class="text-grayDark">/month</span>
          </div>
          <ul class="text-left space-y-3 mb-8">
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Unlimited projects
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Advanced team management
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Talent intelligence
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Priority support
            </li>
          </ul>
          <button class="w-full py-3 px-6 bg-primaryDark text-white rounded-lg hover:bg-opacity-90 transition-colors">
            Get Started
          </button>
        </div>

        <!-- Enterprise Plan -->
        <div class="bg-white border border-grayLight rounded-lg p-8 text-center">
          <h3 class="text-2xl font-bold text-primaryDark mb-4">Enterprise</h3>
          <div class="mb-6">
            <span class="text-4xl font-bold text-primaryDark">Custom</span>
          </div>
          <ul class="text-left space-y-3 mb-8">
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Everything in Pro
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Custom integrations
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              Dedicated support
            </li>
            <li class="flex items-center">
              <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
              SLA guarantee
            </li>
          </ul>
          <button class="w-full py-3 px-6 border border-primaryDark text-primaryDark rounded-lg hover:bg-primaryDark hover:text-white transition-colors">
            Contact Sales
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderComponent from './HeaderComponent.vue';

export default {
  name: 'PricingComponent',
  components: {
    HeaderComponent,
  },
};
</script>